export type Element = "fire" | "ice" | "lightning";
export type GridPos = { x: number; y: number };
export type Piece = { id: string; element: Element; tier: number };
export type Board = { width: number; height: number; cells: Array<Piece | null> };

export type BoardAction =
  | { type: "swap"; a: GridPos; b: GridPos; aPiece: Piece | null; bPiece: Piece | null }
  | { type: "merge"; from: GridPos; to: GridPos; element: Element; oldTier: number; newTier: number };

export type BoardResult = { board: Board; action: BoardAction };

export function createBoard(width = 12, height = 12): Board {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    throw new Error("Board dimensions must be positive integers");
  }
  return { width, height, cells: Array<Piece | null>(width * height).fill(null) };
}

export function isInside(board: Board, pos: GridPos): boolean {
  return Number.isInteger(pos.x) && Number.isInteger(pos.y) &&
    pos.x >= 0 && pos.x < board.width && pos.y >= 0 && pos.y < board.height;
}

function indexFor(board: Board, pos: GridPos): number {
  if (!isInside(board, pos)) throw new Error(`Cell ${pos.x},${pos.y} is outside the board`);
  return pos.y * board.width + pos.x;
}

export function getCell(board: Board, pos: GridPos): Piece | null {
  return board.cells[indexFor(board, pos)];
}

export function withPiece(board: Board, pos: GridPos, piece: Piece | null): Board {
  const next = { ...board, cells: board.cells.slice() };
  next.cells[indexFor(board, pos)] = piece ? { ...piece } : null;
  return next;
}

export function canMerge(source: Piece | null, destination: Piece | null): boolean {
  return Boolean(source && destination && source.id !== destination.id &&
    source.element === destination.element && source.tier === destination.tier);
}

// With a selection, show only its partners; otherwise show every available pair.
export function mergeablePieceIds(board: Board, selected: GridPos | null = null): Set<string> {
  const source = selected ? getCell(board, selected) : null;
  return new Set(board.cells.flatMap(piece => {
    if (!piece) return [];
    const eligible = selected ? canMerge(source, piece) : board.cells.some(other => canMerge(piece, other));
    return eligible ? [piece.id] : [];
  }));
}

// The model decides the move before the scene plays its animation.
export function applyBoardAction(board: Board, from: GridPos, to: GridPos): BoardResult {
  const fromIndex = indexFor(board, from);
  const toIndex = indexFor(board, to);
  if (fromIndex === toIndex) throw new Error("Choose two different cells");

  // Copy pieces too, so later animation or save code cannot change the previous board.
  const cells = board.cells.map((piece) => piece ? { ...piece } : null);
  const source = cells[fromIndex];
  const destination = cells[toIndex];
  if (source && destination && canMerge(source, destination)) {
    cells[fromIndex] = null;
    cells[toIndex] = { ...destination, tier: destination.tier + 1 };
    return {
      board: { ...board, cells },
      action: {
        type: "merge", from: { ...from }, to: { ...to }, element: source.element,
        oldTier: source.tier, newTier: source.tier + 1,
      },
    };
  }

  cells[fromIndex] = destination;
  cells[toIndex] = source;
  return {
    board: { ...board, cells },
    action: {
      type: "swap", a: { ...from }, b: { ...to },
      aPiece: source ? { ...source } : null, bPiece: destination ? { ...destination } : null,
    },
  };
}
